import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Stage,
  StageConnectionState,
  StageEvents,
  SubscribeType,
  type StageParticipantInfo,
  type RemoteStageStream,
  type RemoteVideoStats,
  type SeiMessage,
} from 'amazon-ivs-web-broadcast';

import {
  HEARTBEAT_PERIOD_MS, RENEW_BELOW_S, STATS_PERIOD_MS, mediaConfig,
} from './config.ts';
import { measureClockOffset, type ClockOffset } from './clockOffset.ts';
import {
  SessionGone, heartbeat, leaveSession, openSession, type VideoSession,
} from './videoSession.ts';

/* ==========================================================================
   Live video for the teleop view (PRD-0010, issue #244).

   Three properties of this hook are load-bearing rather than incidental, and
   each corresponds to a way this surface could lie to an operator.

   1. THE HEARTBEAT IS GATED ON PAGE VISIBILITY (D15).
      A session has no hard cap: it lives while somebody is watching and ends
      when the last watcher's heartbeat stops. That makes the heartbeat the ONLY
      bound on a robot's streaming, so a bare setInterval — which keeps firing in
      a backgrounded tab — would defeat it entirely and leave robots publishing
      to nobody. We beat only while the document is visible, and stop the moment
      it is not.

   2. THE PUBLISHER IS VERIFIED, NOT ASSUMED.
      Participant attributes are stamped by our API at mint time and are
      therefore server-vouched. We subscribe ONLY to a participant whose
      attributes match the robot and session we asked for. Authentication proves
      *a* legitimate publisher is on the stage; only this proves it is THIS
      robot's. Without it a stale or mis-scoped publisher renders as live video
      from the robot the operator is looking at — the exact failure this whole
      surface exists to avoid.

   3. NUMBERS ARE MEASURED OR ABSENT.
      Every figure here comes from requestQualityStats() or from the track
      itself. Nothing is seeded, defaulted, or interpolated. Where a statistic
      is unavailable the field is null and the UI renders an em dash.
   ========================================================================== */

export type VideoState =
  | 'idle'          // nothing asked for
  | 'unavailable'   // no media plane configured in this deployment
  | 'starting'      // session opened, robot has been asked to publish
  | 'connecting'    // stage joined, waiting for a track
  | 'live'          // a verified track is rendering
  | 'degraded'      // live, but the link is hurting
  | 'reconnecting'  // lost the stage, or swapping an expiring token
  | 'ended'         // session closed
  | 'error';

export interface VideoStats {
  /** From the track itself. RemoteVideoStats carries no geometry. */
  width: number | null;
  height: number | null;
  /** Derived from framesDecoded over the sample window. */
  fps: number | null;
  /** Derived from bytesReceived over the sample window. */
  bitrateKbps: number | null;
  packetsLost: number | null;
  /** 0 DOWN … 4 EXCELLENT, straight from the SDK. */
  networkQuality: number | null;
  /**
   * Mean jitter-buffer delay, ms. NOT glass-to-glass and never labelled as it
   * (PRD-0010 D10): this is one leg of the receive path, nothing more.
   *
   * It is no longer the only thing on offer. #250 settled that IVS forwards
   * the robot's SEI frame stamps, so `SeiObserved.captureToArrivalMs` now
   * carries a capture-to-arrival figure spanning the whole path. This stays
   * because it is offset-free — it measures one machine's own buffer, so it
   * says nothing about clock skew and is the honest thing to fall back to when
   * the two clocks cannot be trusted against each other.
   */
  jitterBufferMs: number | null;
}

/**
 * What the SUBSCRIBER has actually observed of the publisher's SEI (#250).
 *
 * This exists to answer one question with evidence rather than belief: does IVS
 * forward User Data Unregistered SEI inserted by a THIRD-PARTY WHIP publisher?
 * The robot side is already proven (60/60 access units through h264parse,
 * 32 B/frame); the cloud half was never observable because nothing here ever
 * listened. `count` staying 0 on a live stream is a real answer, not a bug.
 */
export interface SeiObserved {
  /** SEI messages received on this session. 0 with a live stream = not forwarded. */
  count: number;
  /** Bytes in the most recent payload — the robot's stamp is 8 B. */
  lastBytes: number | null;
  /** performance.now() of the most recent message, for a rate if one is wanted. */
  lastAt: number | null;
  /**
   * Capture-to-arrival, ms: `Date.now()` here minus the frame's own
   * `header.stamp` on the robot (D10, #259).
   *
   * IT CARRIES THE CLOCK SKEW BETWEEN THE TWO MACHINES, and that is not a
   * rounding error. Measured against jetrover-002, both ends reporting
   * NTP-synchronised: the robot sat +22 ms from its NTP source and this
   * machine +115 ms from its own — about 90 ms apart, which is the same order
   * as the latency being measured. So this is a real measurement of
   * (true delay + offset), not a precision instrument, and whatever renders it
   * must say so rather than presenting it as glass-to-glass.
   *
   * null until a stamped frame arrives, or when the figure is not credible.
   */
  captureToArrivalMs: number | null;
  /**
   * What was subtracted to get there: this browser's clock offset against the
   * API, and the round trip that bounds its accuracy. null when the offset
   * could not be measured, in which case `captureToArrivalMs` is uncorrected
   * and carries the full skew — which is what `clockOffset === null` tells a
   * caller that wants to say so.
   */
  clock: ClockOffset | null;
}

const NO_SEI: SeiObserved = {
  count: 0, lastBytes: null, lastAt: null, captureToArrivalMs: null,
  clock: null,
};

/**
 * Beyond this, the number is telling us about the clocks rather than the link.
 * A frame that left the robot before it was captured (negative) or is
 * seconds old is skew or an unsynchronised host, and reporting it as latency
 * would be exactly the invented figure this console refuses to draw.
 */
const SEI_DELAY_SANE_MS = 5_000;

const NO_STATS: VideoStats = {
  width: null, height: null, fps: null, bitrateKbps: null,
  packetsLost: null, networkQuality: null, jitterBufferMs: null,
};

/** Previous sample, so rates can be differenced rather than invented. */
interface StatSample {
  t: number;
  bytesReceived: number | null;
  framesDecoded: number | null;
}

export interface LiveVideo {
  state: VideoState;
  /** Why we are not showing a picture. Always set when state is not `live`. */
  reason: string | null;
  stream: MediaStream | null;
  stats: VideoStats;
  /** What SEI the subscriber has actually seen on this session (#250). */
  sei: SeiObserved;
  viewers: number | null;
  session: VideoSession | null;
  start: () => void;
  stop: () => void;
}

export function useVideoSession(robotId: string | undefined,
                                opts: { cameraTopic?: string;
                                        record?: boolean } = {}): LiveVideo {
  const configured = mediaConfig() !== null;

  const [state, setState] = useState<VideoState>('idle');
  const [reason, setReason] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [stats, setStats] = useState<VideoStats>(NO_STATS);
  const [viewers, setViewers] = useState<number | null>(null);
  const [session, setSession] = useState<VideoSession | null>(null);
  const [sei, setSei] = useState<SeiObserved>(NO_SEI);
  // Read inside the SEI handler, which fires per frame — a ref rather than
  // state so a re-measure does not re-subscribe the stage.
  const clockRef = useRef<ClockOffset | null>(null);
  const [wanted, setWanted] = useState(false);
  // Bumped on every start(). Without it, pressing Start while `wanted` is
  // already true is a no-op React optimises away entirely — the effect's deps
  // do not change, nothing runs, and the operator gets no feedback at all.
  // That is a dead control, which is worse than a failing one: after an error
  // the obvious recovery (press it again) did nothing and said nothing.
  const [attempt, setAttempt] = useState(0);

  // Mutable session state the effects share without re-running.
  const stageRef = useRef<Stage | null>(null);
  const remoteRef = useRef<RemoteStageStream | null>(null);
  const tokenExpiryRef = useRef<number>(0);      // epoch ms
  const sessionRef = useRef<VideoSession | null>(null);
  const prevSampleRef = useRef<StatSample | null>(null);
  const cameraTopic = opts.cameraTopic;
  /* D11. Read at START only, deliberately: recording is a property of the
     session, and a session already running belongs to everyone watching it.
     Toggling this mid-stream must not reconfigure a colleague's view — the
     API refuses it for a joiner for the same reason. */
  const record = opts.record === true;

  const fail = useCallback((why: string) => {
    setState('error');
    setReason(why);
  }, []);

  /* ---- verification (property 2) --------------------------------------- */

  const matchesExpected = useCallback((p: StageParticipantInfo): boolean => {
    const s = sessionRef.current;
    if (!s) return false;
    const a = (p.attributes ?? {}) as Record<string, string>;
    // The robot's own publisher, for the session we asked for. Anything else —
    // another robot, another session, an operator's participant — is not our
    // picture and must not be drawn as though it were.
    return a.role === 'robot' && a.robot_id === s.robot_id
      && a.session_id === s.session_id;
  }, []);

  /* ---- the session + stage lifecycle ----------------------------------- */

  const teardown = useCallback((next: VideoState, why: string | null) => {
    try { stageRef.current?.leave(); } catch { /* already gone */ }
    stageRef.current = null;
    remoteRef.current = null;
    setStream(null);
    setStats(NO_STATS);
    setState(next);
    setReason(why);
  }, []);

  const joinStage = useCallback(async (s: VideoSession) => {
    const strategy = {
      stageStreamsToPublish: () => [],
      // The console NEVER publishes. Its token is subscribe-only, so this is
      // belt and braces on top of a credential that could not publish anyway.
      shouldPublishParticipant: () => false,
      shouldSubscribeToParticipant: (p: StageParticipantInfo) =>
        (matchesExpected(p) ? SubscribeType.AUDIO_VIDEO : SubscribeType.NONE),
      // WITHOUT THIS THE SEI HANDLER BELOW NEVER FIRES. `inBandMessaging` is
      // opt-in and defaults to DISABLED, so a listener alone would sit silent
      // and read as "IVS drops third-party SEI" when the truth is that nobody
      // asked for it. Whoever answers #250 must keep these two together.
      subscribeConfiguration: (p: StageParticipantInfo) =>
        (matchesExpected(p) ? { inBandMessaging: { enabled: true } } : {}),
    };

    const stage = new Stage(s.token, strategy);
    stageRef.current = stage;
    setSei(NO_SEI);   // per session: a previous stream's count proves nothing

    // #250. Counted, not just logged, so the answer survives a closed console
    // and can be read off the running app.
    stage.on(StageEvents.STAGE_STREAM_SEI_MESSAGE_RECEIVED,
      (p: StageParticipantInfo, message: SeiMessage) => {
        if (!matchesExpected(p)) return;

        // The payload is the robot's `header.stamp` for this frame: 8 bytes,
        // big-endian nanoseconds since the Unix epoch. The uuid is stripped by
        // the SDK into `message.uuid`, which is why 24 bytes go on the wire and
        // 8 arrive here.
        let delay: number | null = null;
        try {
          // The SDK types this as ArrayBuffer, and it is not always one — a
          // typed-array view arrives here in practice, and `new DataView(view)`
          // THROWS rather than coercing. Thrown from this handler it took the
          // whole callback with it, so the SEI never even got counted and the
          // symptom was "IVS stopped forwarding" while the robot was plainly
          // still stamping. Both shapes are accepted, and any parse failure
          // costs the reading, never the count.
          const pl = message.payload as ArrayBuffer | ArrayBufferView;
          const view = ArrayBuffer.isView(pl)
            ? new DataView(pl.buffer, pl.byteOffset, pl.byteLength)
            : new DataView(pl);
          if (view.byteLength === 8) {
            const captureMs = Number(view.getBigUint64(0, false) / 1_000_000n);
            // Both instants moved onto the SERVER's clock before subtracting.
            // The robot's stamp is already close to it (+7.5 ms measured); this
            // browser may not be (+183.8 ms measured on one laptop), and that
            // difference lands in the reading one-for-one. With no measurement
            // the correction is 0 and the figure carries the skew, which is
            // what `sei.clock === null` exists to disclose.
            const d = (Date.now() + (clockRef.current?.offsetMs ?? 0)) - captureMs;
            // Negative means the frame was captured in our future, which is
            // skew, not a link that beat causality.
            if (d >= 0 && d < SEI_DELAY_SANE_MS) delay = d;
          }
        } catch {
          // Leave `delay` null; the message still counts as received.
        }

        setSei((cur) => {
          if (cur.count === 0) {
            // Once, on the first one: the fact #250 asks for, stated plainly.
            console.info('[video] SEI forwarded by IVS from a third-party WHIP '
              + `publisher — ${message.payload.byteLength} B payload`);
          }
          return {
            count: cur.count + 1,
            lastBytes: (message.payload as ArrayBufferView | ArrayBuffer).byteLength,
            lastAt: performance.now(),
            // Keep the last CREDIBLE figure rather than blanking on one bad
            // sample: a single implausible stamp is noise, and a gauge that
            // flickers to an em dash and back is harder to read than one that
            // holds its last real reading.
            captureToArrivalMs: delay ?? cur.captureToArrivalMs,
            clock: clockRef.current,
          };
        });
      });

    stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (cs: StageConnectionState) => {
      if (cs === StageConnectionState.CONNECTED) {
        setState((cur) => (cur === 'live' || cur === 'degraded' ? cur : 'connecting'));
        setReason('stage joined; waiting for the robot to publish');
      } else if (cs === StageConnectionState.CONNECTING) {
        setState((cur) => (cur === 'idle' ? cur : 'reconnecting'));
      } else if (cs === StageConnectionState.DISCONNECTED) {
        setState((cur) => (cur === 'ended' ? cur : 'reconnecting'));
        setReason('stage disconnected');
      }
    });

    stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED,
      (p: StageParticipantInfo, streams: { mediaStreamTrack: MediaStreamTrack }[]) => {
        if (!matchesExpected(p)) {
          // Loud on purpose: this is the case where something is publishing
          // into our stage that should not be, and silence would render it.
          console.warn('[video] refusing an unverified publisher', p.attributes);
          return;
        }
        const video = streams.find((st) => st.mediaStreamTrack.kind === 'video');
        if (!video) return;
        // The event's element type is the generic StageStream; only a remote
        // one carries requestQualityStats, which is what the instruments need.
        remoteRef.current = video as unknown as RemoteStageStream;
        setStream(new MediaStream([video.mediaStreamTrack]));
        setState('live');
        setReason(null);
      });

    stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (p: StageParticipantInfo) => {
      if (!matchesExpected(p)) return;
      remoteRef.current = null;
      setStream(null);
      setState((cur) => (cur === 'ended' ? cur : 'reconnecting'));
      setReason('the robot stopped publishing');
    });

    await stage.join();
  }, [matchesExpected]);

  // Open (or join) a session, then join its stage.
  useEffect(() => {
    if (!wanted || !robotId || !configured) return;
    let cancelled = false;

    (async () => {
      setState('starting');
      setReason('asking the robot to publish');
      try {
        const s = await openSession(robotId, {
          ...(cameraTopic ? { cameraTopic } : {}),
          ...(record ? { record: true } : {}),
        });
        if (cancelled) { void leaveSession(robotId, s.session_id); return; }
        sessionRef.current = s;
        tokenExpiryRef.current = Date.now() + s.expires_in_s * 1000;
        setSession(s);
        setViewers(s.viewers);
        await joinStage(s);
      } catch (err) {
        if (!cancelled) fail(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
      const s = sessionRef.current;
      sessionRef.current = null;
      teardown('ended', 'you stopped watching');
      if (s && robotId) void leaveSession(robotId, s.session_id);
    };
  }, [wanted, attempt, robotId, configured, cameraTopic, record, joinStage, teardown, fail]);

  /* ---- the heartbeat, gated on visibility (property 1) ------------------ */

  useEffect(() => {
    if (!wanted || !robotId) return;

    let timer: number | undefined;

    const beat = async () => {
      const s = sessionRef.current;
      if (!s || document.visibilityState !== 'visible') return;
      const leftS = Math.max(0, (tokenExpiryRef.current - Date.now()) / 1000);
      try {
        const hb = await heartbeat(robotId, s.session_id, leftS);
        setViewers(hb.viewers);
        if (hb.token && hb.expires_in_s) {
          // The SDK takes its token at construction, so a renewal means a
          // rejoin. Done proactively here rather than waiting to be cut off, so
          // the operator sees a brief, LABELLED reconnect instead of a freeze.
          sessionRef.current = { ...s, token: hb.token, expires_in_s: hb.expires_in_s };
          tokenExpiryRef.current = Date.now() + hb.expires_in_s * 1000;
          if (leftS < RENEW_BELOW_S / 2) {
            setState('reconnecting');
            setReason('renewing the session token');
            try { stageRef.current?.leave(); } catch { /* ignore */ }
            await joinStage(sessionRef.current);
          }
        }
      } catch (err) {
        if (err instanceof SessionGone) {
          sessionRef.current = null;
          teardown('ended', 'the session ended');
        }
        // Any other failure is a transient: the next beat retries, and the
        // reaper is the backstop if they never succeed.
      }
    };

    const schedule = () => {
      window.clearInterval(timer);
      // Only run the interval while visible. A hidden tab beats zero times,
      // which is what makes the heartbeat a real bound rather than a formality.
      if (document.visibilityState === 'visible') {
        void beat();
        timer = window.setInterval(() => { void beat(); }, HEARTBEAT_PERIOD_MS);
      }
    };

    schedule();
    document.addEventListener('visibilitychange', schedule);

    // A closing tab gets one last chance to hand the session back, so the robot
    // stops now rather than when the reaper next runs.
    const onHide = () => {
      const s = sessionRef.current;
      if (s && robotId) void leaveSession(robotId, s.session_id, { keepalive: true });
    };
    window.addEventListener('pagehide', onHide);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', schedule);
      window.removeEventListener('pagehide', onHide);
    };
  }, [wanted, robotId, joinStage, teardown]);

  /* ---- this browser's clock, measured against the API's (#259) --------- */

  useEffect(() => {
    if (!wanted || !configured) return;
    const ctl = new AbortController();
    let timer = 0;

    const take = async () => {
      const got = await measureClockOffset(ctl.signal);
      // Keep the previous estimate if a round fails. A clock does not become
      // unknown because the network hiccuped, and dropping to "no correction"
      // would silently put the whole skew back into the reading.
      if (got !== null) clockRef.current = got;
    };

    void take();
    // Re-measured, not measured once: a laptop resuming from sleep steps its
    // clock, and NTP slews continuously. Ten minutes is far more often than
    // either matters and still nothing next to a heartbeat every 15 s.
    timer = window.setInterval(() => { void take(); }, 10 * 60 * 1000);

    return () => { ctl.abort(); window.clearInterval(timer); };
  }, [wanted, configured]);

  /* ---- measured instruments (property 3) ------------------------------- */

  useEffect(() => {
    if (state !== 'live' && state !== 'degraded') return;
    const id = window.setInterval(async () => {
      const rs = remoteRef.current;
      if (!rs) return;
      try {
        const got = await rs.requestQualityStats();
        const arr = Array.isArray(got) ? got : [got];
        // Remote stats are a union with the audio shape; pick the video one by
        // the field only it has rather than by position.
        const v = arr.find((x): x is RemoteVideoStats =>
          Boolean(x) && 'framesDecoded' in (x as object)) ?? null;
        if (!v) { setStats(NO_STATS); return; }

        // RemoteVideoStats has no frameWidth / frameHeight / bitrate — those
        // live on the LOCAL stats, i.e. on the publisher. A subscriber
        // differences counters instead of reading rates, which is also why the
        // first sample reports no rates at all.
        //
        // `getSettings()` IS ASKED, AND ON A REMOTE TRACK IT DOES NOT ANSWER.
        // Measured against a live 640x360 stream in Chrome it returns exactly
        // `{deviceId, resizeMode}` — no geometry, ever. So width/height here
        // are effectively always null for a subscriber. They are left in place
        // because a browser that does populate them is welcome to, but nothing
        // should DEPEND on them: the consumer reads the decoded frame off the
        // <video> element instead (Teleop.tsx `frameSize`), which is the only
        // place the true geometry exists and the only thing that notices when a
        // publisher changes it mid-session.
        const settings = rs.mediaStreamTrack.getSettings();
        const now = Date.now();
        const prev = prevSampleRef.current;
        const dt = prev ? (now - prev.t) / 1000 : 0;

        const bytes = v.bytesReceived ?? null;
        const frames = v.framesDecoded ?? null;
        const bitrateKbps = (prev && dt > 0 && bytes !== null && prev.bytesReceived !== null)
          ? Math.max(0, Math.round(((bytes - prev.bytesReceived) * 8) / dt / 1000))
          : null;
        const fps = (prev && dt > 0 && frames !== null && prev.framesDecoded !== null)
          ? Math.max(0, Math.round((frames - prev.framesDecoded) / dt))
          : null;
        prevSampleRef.current = { t: now, bytesReceived: bytes, framesDecoded: frames };

        const emitted = v.jitterBufferEmittedCount ?? 0;
        const jitterBufferMs = (v.jitterBufferDelay !== undefined && emitted > 0)
          ? Math.round((v.jitterBufferDelay / emitted) * 1000)
          : null;

        const next: VideoStats = {
          width: settings.width ?? null,
          height: settings.height ?? null,
          fps,
          bitrateKbps,
          packetsLost: v.packetsLost ?? null,
          networkQuality: v.networkQuality ?? null,
          jitterBufferMs,
        };
        setStats(next);

        // "Degraded" is a MEASURED claim, not a guess: the SDK's own network
        // quality (POOR or DOWN), or frames that have genuinely stopped. Note
        // fps === null means "not measured yet", not "zero" — a first sample
        // must never be reported as a stall.
        const bad = (next.networkQuality !== null && next.networkQuality <= 1)
          || (next.fps !== null && next.fps < 5);
        setState((cur) => (cur === 'live' && bad ? 'degraded'
          : cur === 'degraded' && !bad ? 'live' : cur));
        setReason(bad ? 'the link is degraded' : null);
      } catch {
        // Stats being unavailable is not the same as a bad link. Blank them
        // rather than inventing a reading or claiming degradation.
        setStats(NO_STATS);
        prevSampleRef.current = null;
      }
    }, STATS_PERIOD_MS);
    return () => { window.clearInterval(id); };
  }, [state]);

  const start = useCallback(() => {
    if (!configured) {
      setState('unavailable');
      setReason('no media plane configured in this deployment');
      return;
    }
    setWanted(true);
    setAttempt((n) => n + 1);   // always advances, so a retry always retries
  }, [configured]);

  const stop = useCallback(() => setWanted(false), []);

  return {
    state: configured ? state : (state === 'idle' ? 'idle' : 'unavailable'),
    reason, stream, stats, sei, viewers, session, start, stop,
  };
}
